import QRCode from 'qrcode';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import thyagaLogo from '../../styles/images/login_logo_high_res.png';
import { formatAmount, buildCheckBalanceUrl } from '../ViewVouchers/ViewVouchersUtils';
import { formatDateTime } from '../../utils/dataFormatter';
import { DATE_TIME_TYPES } from '../../constants/dataTimeTypes';
import { DECOR_ICONS } from '../../constants/purchaseHistoryPDF';
import { searchVouchers } from '../../api/Vouchers';

const COLORS = {
  primary: '#210842',
  text: '#332e46',
  accent: '#a78dc0',
  border: '#e4defa',
  bottomBg: '#f3f2f8',
  dashed: '#c9c2e0',
  highlight: '#ed7d31',
};

// Drops a trailing ".00" from a formatAmount() string (e.g. "1,500.00" -> "1,500")
// so whole-number vouchers print without redundant zeros; keeps real cents (e.g. "1,500.50").
const formatAmountForPdf = (amount) => formatAmount(amount).replace(/\.00$/, '');

const getSenderName = (data, voucher) => {
  return (
    (voucher && (voucher.senderName || voucher.sender)) ||
    data.sender ||
    (data.createdBy && data.createdBy.id) ||
    'Thyaga'
  );
};

const getValidityLine = (voucher) => {
  if (voucher && voucher.expiryDate) {
    return `Valid until ${formatDateTime(voucher.expiryDate, DATE_TIME_TYPES.DATE)}`;
  }
  return 'Valid for 6 months from the date of issue';
};

const fetchVoucherThemeImageUrl = async (voucherId, token) => {
  try {
    const request = {
      searchKey: voucherId,
      searchId: 'id',
      page: 0,
      pageSize: 1,
      sortBy: 'created_date',
      order: 'desc',
    };
    const response = await searchVouchers(request, token);
    const voucher = response && response.list && response.list[0];
    return (voucher && voucher.imageFront) || null;
  } catch (error) {
    return null;
  }
};

const preloadThemeImage = (src) => {
  return new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(src);
    img.onerror = () => resolve(null);
    img.src = src;
  });
};

const waitForImages = (root) => {
  const images = Array.from(root.querySelectorAll('img'));
  return Promise.all(
    images.map((img) => {
      if (img.complete && img.naturalWidth !== 0) return Promise.resolve();
      return new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
    })
  );
};

const buildCardImageHtml = (themeImageUrl) => {
  if (themeImageUrl) {
    return `
      <div style="flex:1 1 auto; aspect-ratio:1.586; align-self:center; border-radius:16px; overflow:hidden; display:flex;">
        <img src="${themeImageUrl}" crossorigin="anonymous" style="width:100%; height:100%; object-fit:cover; display:block; border-radius:16px;" />
      </div>
    `;
  }

  return `
    <div style="flex:1 1 auto; aspect-ratio:1.586; align-self:center; border-radius:16px; overflow:hidden; display:flex; background:#ffffff; border:1px solid ${COLORS.primary}; align-items:center; justify-content:center;">
      <img src="${thyagaLogo}" crossorigin="anonymous" style="width:35%; height:auto; display:block;" />
    </div>
  `;
};

const SPECIAL_TITLE_THEME_IDS = [273, 1059, 1060];
const SPECIAL_VOUCHER_TITLE = 'Let Me Make It Up to You!';
const DEFAULT_VOUCHER_TITLE = 'Ta Daaa!';
const RECEIVED_MESSAGE = "You've received a Thyaga Voucher";

const resolveVoucherTitle = (themeId) => {
  return SPECIAL_TITLE_THEME_IDS.includes(Number(themeId))
    ? SPECIAL_VOUCHER_TITLE
    : DEFAULT_VOUCHER_TITLE;
};

const buildVoucherHtml = ({ voucherId, amount, from, qrDataUrl, validityLine, themeImageUrl, title }) => {
  return `
    <div style="position:relative; width:920px; background-color:#ffffff; font-family:'Poppins', Arial, sans-serif; color:${COLORS.text}; overflow:hidden; box-sizing:border-box;">
      <div style="position:relative; padding:50px 60px 40px; box-sizing:border-box;">
        <h1 style="text-align:center; font-size:42px; font-weight:700; color:${COLORS.text}; margin:0 0 8px;">${title}</h1>
        <h2 style="text-align:center; font-size:24px; font-weight:500; color:${COLORS.text}; margin:0 0 40px;">${RECEIVED_MESSAGE}</h2>

        <div style="position:relative; display:flex; align-items:stretch; gap:24px; width:85%; margin:0 auto; border:2px solid ${COLORS.border}; border-radius:24px; padding:24px; box-sizing:border-box;">
          <img src="${DECOR_ICONS.starThree}" crossorigin="anonymous" alt="" style="position:absolute; width:75px; height:auto; top:-64px; right:-50px;" />
          <img src="${DECOR_ICONS.starTwo}" crossorigin="anonymous" alt="" style="position:absolute; width:75px; height:auto; bottom:-40px; left:-40px;" />
          <img src="${DECOR_ICONS.orange}" crossorigin="anonymous" alt="" style="position:absolute; width:90px; height:auto; top:50%; left:-165px; transform:translateY(-50%); opacity:0.9;" />
          <img src="${DECOR_ICONS.green}" crossorigin="anonymous" alt="" style="position:absolute; width:90px; height:auto; top:50%; right:-165px; transform:translateY(-50%); opacity:0.9;" />

          ${buildCardImageHtml(themeImageUrl)}

          <div style="flex:0 0 190px; height:100%; display:flex; flex-direction:column; align-items:center;">
            <div style="width:100%; aspect-ratio:1/1; border:2px solid ${COLORS.border}; border-radius:16px; display:flex; align-items:center; justify-content:center; overflow:hidden; box-sizing:border-box;">
              <img src="${qrDataUrl}" style="width:82%; height:82%; object-fit:contain;" />
            </div>
            <div style="margin-top:18px; font-size:15px; color:${COLORS.text};">Voucher ID :</div>
            <div style="font-size:17px; font-weight:700; color:${COLORS.text};">${voucherId}</div>
            <div style="margin-top:auto; padding-top:16px; font-size:24px; font-weight:700; color:${COLORS.text}; text-align:center;">LKR ${amount}/=</div>
          </div>
        </div>

        <div style="margin-top:48px; text-align:center; display:flex; flex-direction:column; gap:4px;">
          <span style="font-size:18px; color:${COLORS.text};">From</span>
          <span style="font-size:19px; font-weight:700; color:${COLORS.text};">${from}</span>
        </div>
      </div>

      <div style="background-color:${COLORS.bottomBg}; padding:40px 60px 40px; box-sizing:border-box;">
        <div style="display:flex; align-items:center; justify-content:center; gap:16px; font-size:20px; font-weight:700; color:${COLORS.text}; margin-bottom:20px;">
          <span style="flex:1 1 auto; max-width:160px; border-top:2px dashed ${COLORS.dashed};"></span>
          <span>How to Redeem</span>
          <span style="flex:1 1 auto; max-width:160px; border-top:2px dashed ${COLORS.dashed};"></span>
        </div>

        <div style="display:flex; gap:20px; border:2px dashed ${COLORS.dashed}; border-radius:16px; padding:24px 28px; box-sizing:border-box;">
          <div style="flex:1 1 0; font-size:15px; color:${COLORS.text}; line-height:1.5;">
            <strong style="display:block; font-size:19px; margin-bottom:4px;">1. Scan</strong> the QR code to view your voucher
          </div>
          <div style="flex:1 1 0; font-size:15px; color:${COLORS.text}; line-height:1.5;">
            <strong style="display:block; font-size:19px; margin-bottom:4px;">2. Verify</strong> with OTP to access it securely
          </div>
          <div style="flex:1 1 0; font-size:15px; color:${COLORS.text}; line-height:1.5;">
            <strong style="display:block; font-size:19px; margin-bottom:4px;">3. Choose</strong> a merchant and redeem fully or partially
          </div>
        </div>

        <div style="text-align:center; margin-top:40px;">
          <div style="font-size:18px; font-weight:600; color:${COLORS.highlight};">Use this voucher at</div>
          <div style="font-size:24px; font-weight:700; color:${COLORS.highlight};">300+ partner merchants across Sri Lanka</div>
          <img src="${DECOR_ICONS.underline}" crossorigin="anonymous" alt="" style="display:block; width:280px; height:auto; margin:-55px auto 0;" />
        </div>

        <div style="text-align:center; margin-top:30px;">
          <div style="font-size:18px; font-weight:700; color:${COLORS.text}; margin-bottom:10px;">Terms and Conditions</div>
          <div style="font-size:13px; color:${COLORS.text}; line-height:1.7;">
            ${validityLine}<br/>
            Redeemable only at Thyaga partner merchants<br/>
            Cannot be exchanged for cash<br/>
            Please follow the instructions on the Thyaga website to redeem
          </div>
        </div>

        <div style="margin-top:30px; display:flex; align-items:center; justify-content:space-between; border:2px solid ${COLORS.highlight}; border-radius:50px; padding:16px 32px; box-sizing:border-box;">
          <div style="display:flex; flex-direction:column; gap:2px; font-size:15px; color:${COLORS.text};">
            <strong>Need help?</strong>
            <span>Contact us: +94 750 100 500</span>
          </div>
          <img src="${thyagaLogo}" style="height:30px;" />
        </div>
      </div>

      <div style="background-color:${COLORS.primary}; color:#ffffff; text-align:center; font-size:14px; padding:18px 0;">www.<strong>thyaga</strong>.lk</div>
    </div>
  `;
};

export const generatePurchaseVoucherPdf = async (drawerData, token) => {

  const voucher = drawerData && drawerData.voucher;
  const voucherId = voucher && voucher.id;

  if (!voucherId) {
    throw new Error('No voucher associated with this purchase');
  }

  const checkBalanceUrl = buildCheckBalanceUrl(voucherId);
  const qrDataUrl = await QRCode.toDataURL(checkBalanceUrl, {
    width: 300,
    margin: 1,
    color: { dark: COLORS.primary, light: '#ffffff' },
  });

  const fetchedImageFront = await fetchVoucherThemeImageUrl(voucherId, token);
  const themeImageUrl = await preloadThemeImage(fetchedImageFront || voucher.imageFront);

  const voucherThemeId = voucher.coreVoucherTheme && voucher.coreVoucherTheme.id;

  const voucherHtmlData = {
    voucherId,
    amount: formatAmountForPdf(voucher.amount ?? drawerData.amount),
    from: getSenderName(drawerData, voucher),
    qrDataUrl,
    validityLine: getValidityLine(voucher),
    themeImageUrl,
    title: resolveVoucherTitle(voucherThemeId),
  };

  const html = buildVoucherHtml(voucherHtmlData);

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.top = '0';
  container.style.left = '-10000px';
  container.innerHTML = html;
  document.body.appendChild(container);

  try {
    await waitForImages(container);

    const canvas = await html2canvas(container.firstElementChild, {
      scale: 1.5,
      useCORS: true,
      backgroundColor: '#ffffff',
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.85);

    const pageWidth = 210; 
    const pageHeight = (canvas.height / canvas.width) * pageWidth;

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [pageWidth, pageHeight],
      compress: true,
    });
    pdf.addImage(imgData, 'JPEG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');
    pdf.save(`Thyaga_Voucher_${voucherId}.pdf`);
  } finally {
    document.body.removeChild(container);
  }
};
